import { Component, Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { IDLE_SNAPSHOT, SyncContext, SyncReady, type SyncActions, type SyncCtx } from './sync-state.ts';

const SyncBridge = lazy(() => import('./sync-bridge.tsx'));

/** A failed download of the engine must not take the whole app down: the screens keep their idle state. */
class Quiet extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** Longest wait for the first screen to finish before the engine starts anyway. */
const MAX_DEFER_MS = 8000;

/**
 * Offline sync for the signed-in app without putting the engine on the critical path of the first screen. The
 * children render at once (panel, header); the engine (sync-context.tsx with Dexie, about 25 KB compressed and the
 * heaviest script to evaluate after React) starts when the first screen has its data and the browser is idle, or at
 * once when a screen reads it (movement, sync: useSync asks for it). Until it runs, the
 * context holds an idle snapshot ("Conectado") and engine calls wait for it. Measured on the Lighthouse phone profile
 * (docs/reviews/PERFORMANCE_REPORT.md): loading it together with the panel delayed the panel's largest paint. Once
 * loaded it is SyncProvider itself: same manager, same effects (outbox flush, 72 h clean-up).
 */
export function SyncHost({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<SyncCtx | null>(null);
  const [started, setStarted] = useState(false);
  const [ready] = useState(() => {
    let resolve: (v: SyncCtx) => void = () => {};
    const engine = new Promise<SyncCtx>((r) => (resolve = r));
    const manager: SyncActions = {
      saveOffline: async (...a) => (await engine).manager.saveOffline(...a),
      flush: async (...a) => (await engine).manager.flush(...a),
      discardLocal: async (...a) => (await engine).manager.discardLocal(...a),
      reapplyOnServerRevision: async (...a) => (await engine).manager.reapplyOnServerRevision(...a),
    };
    const placeholder: SyncCtx = { manager, snapshot: IDLE_SNAPSHOT, persistent: null };
    // Called while a screen renders: the state change waits for the render to end.
    const demand = () => queueMicrotask(() => setStarted(true));
    return { resolve, placeholder, demand };
  });
  const fetching = useIsFetching();
  useEffect(() => {
    if (started) return;
    const latest = setTimeout(() => setStarted(true), MAX_DEFER_MS);
    if (fetching > 0) return () => clearTimeout(latest);
    const go = () => setStarted(true);
    if (typeof requestIdleCallback !== 'function') {
      const t = setTimeout(go, 500);
      return () => {
        clearTimeout(latest);
        clearTimeout(t);
      };
    }
    const idle = requestIdleCallback(go, { timeout: 2000 });
    return () => {
      clearTimeout(latest);
      cancelIdleCallback(idle);
    };
  }, [fetching, started]);
  const publish = useCallback(
    (v: SyncCtx) => {
      // The provider builds a new object on every render: keep the previous one when nothing changed (no loop).
      setValue((prev) =>
        prev && prev.manager === v.manager && prev.snapshot === v.snapshot && prev.persistent === v.persistent
          ? prev
          : v,
      );
      ready.resolve(v);
    },
    [ready],
  );
  return (
    <SyncReady.Provider value={ready}>
      <SyncContext.Provider value={value ?? ready.placeholder}>
        {started ? (
          <Quiet>
            <Suspense fallback={null}>
              <SyncBridge onValue={publish} />
            </Suspense>
          </Quiet>
        ) : null}
        {children}
      </SyncContext.Provider>
    </SyncReady.Provider>
  );
}
